//Generated by AI, Needs to be reviewed
import { FC, useRef, useState, ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import addIcon from '@/assets/icons/add.svg';
import minusIcon from '@/assets/icons/minus.svg';

interface CustomDescriptionProps {
  description: string;
  onDescriptionChange: (description: string) => void;
  onRemove: () => void;
}

interface FileUploadProps {
  label?: string;
  placeholder: string;
  value?: File | null;
  onChange: (file: File | null) => void;
  disabled?: boolean;
  required?: boolean;
  accept?: string;
  width?: string;
  // For documents with custom description - if provided, shows description input mode with remove button
  customDescription?: CustomDescriptionProps;
}

const FileUpload: FC<FileUploadProps> = ({
  placeholder,
  value,
  onChange,
  disabled = false,
  accept,
  width = '100%',
  customDescription,
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  
  const hasCustomDescription = !!customDescription;

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (file && file.size === 0) {
      setLocalError(t('emptyFileSelected', { name: file.name }));
      e.target.value = '';
      onChange(null);
      return;
    }
    setLocalError(null);
    onChange(file);
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveFile = () => {
    onChange(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDescriptionInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (customDescription) {
      customDescription.onDescriptionChange(e.target.value);
    }
  };

  // CRITICAL: sub-views are rendered as inline JSX (not as inner React
  // components) so the underlying <input type="file"> keeps the same identity
  // across re-renders. When this used to be `const HiddenFileInput = () => ...`
  // declared inside FileUpload, every parent state update created a brand-new
  // function reference, React treated it as a different component type and
  // unmounted+remounted the <input>. Some browsers then invalidated the
  // already-selected File (file.size became 0 by the time the form was
  // submitted), which surfaced as a confusing 400 from the upload endpoint.
  const hiddenFileInput = (
    <input
      ref={fileInputRef}
      type="file"
      onChange={handleFileChange}
      disabled={disabled}
      accept={accept}
      className="hidden"
    />
  );

  const uploadButton = (
    <button
      type="button"
      onClick={handleButtonClick}
      disabled={disabled}
      className={`h-7 px-2 rounded border border-dashed flex items-center gap-1.5 body-sm-semibold transition-colors ${
        disabled
          ? 'bg-(--surface-disabled) border-(--border-disabled) text-(--font-disabled)'
          : 'bg-(--surface-action) border-(--border-primary) text-(--font-primary) hover:bg-(--surface-primary-lowest)'
      }`}
    >
      {t('uploadFile')}
      <img src={addIcon} alt="" />
    </button>
  );

  const fileDisplay = (
    <div className="bg-(--surface-base) border border-(--border-strong) rounded h-8 px-3 flex items-center gap-2">
      <button
        type="button"
        onClick={handleRemoveFile}
        className="w-[10px] h-[10px] flex items-center justify-center"
        disabled={disabled}
      >
        <svg width="7" height="7" viewBox="0 0 7 7" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M1 1L6 6M6 1L1 6"
            stroke="#888888"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      <div className="w-0 h-[10px] border-r border-(--border-main)" />
      <span className="body-sm-regular text-(--font-body-5)">{value?.name}</span>
    </div>
  );

  return (
    <div className="flex flex-col gap-1.5 relative" style={{ width }}>
      {/* Remove Button */}
      {hasCustomDescription && (
        <>
          <button
            type="button"
            onClick={customDescription.onRemove}
            className="absolute start-[-12px] top-0 w-6 h-6 bg-(--font-error) rounded-full flex items-center justify-center hover:opacity-80 transition-opacity z-10"
            title={t('removeDocument')}
          >
            <img src={minusIcon} alt="" />
          </button>

          {/* Document Input with Custom Description */}
          <div
            className={`flex items-center justify-end gap-4 h-11 px-4 py-2 rounded-md ${
              disabled ? 'border border-(--border-input-default) bg-(--surface-base)' : 'bg-(--surface-grey-low-2)'
            }`}
          >
            {hiddenFileInput}

            {/* Description Input Field - on the RIGHT in RTL */}
            <input
              type="text"
              value={customDescription.description}
              onChange={handleDescriptionInputChange}
              placeholder={placeholder}
              disabled={disabled}
              className={`body-md-regular flex-1 text-right bg-transparent border-0 border-b border-(--border-input-default) outline-none pb-0.5 ${
                disabled ? 'text-(--font-disabled)' : 'text-(--font-heading)'
              } placeholder:text-(--font-body-3)`}
            />

            {/* Upload Button or File Display - on the LEFT in RTL */}
            {value ? fileDisplay : uploadButton}
          </div>
        </>
      )}

      {/* Regular File Upload (for required documents) */}
      {!hasCustomDescription && (
        <div
          className={`flex items-center justify-end gap-4 h-11 px-4 py-2 rounded-md ${
            disabled
              ? 'border border-(--border-input-default) bg-(--surface-base)'
              : value
              ? 'border border-(--border-disabled) bg-(--surface-base)'
              : 'bg-(--surface-grey-low-2)'
          }`}
        >
          {hiddenFileInput}

          {/* Placeholder Text - on the RIGHT in RTL */}
          <span className={`body-md-regular flex-1 text-right overflow-hidden text-ellipsis whitespace-nowrap ${
            disabled ? 'text-(--font-disabled)' : 'text-(--font-body-3)'
          }`}>
            {placeholder}
          </span>

          {/* Upload Button or File Display - on the LEFT in RTL */}
          {value ? fileDisplay : uploadButton}
        </div>
      )}
      {localError && (
        <span role="alert" className="body-sm-regular text-(--font-error)">
          {localError}
        </span>
      )}
    </div>
  );
};

export default FileUpload;

